import { Accordion, AccordionDetails, AccordionSummary, Grid, Typography } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ErrorIcon from '@material-ui/icons/ReportProblemOutlined';
import { useScrollButtonState } from '@ncr-retail/hooks';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { batch, useSelector } from 'react-redux';
import { closeAllItems } from 'src/all-items/all-items';
import BaseButton from 'src/common/components/base-button';
import { codeContainerStyles } from 'src/common/components/code-container/components/code-container-styles';
import { setHomePage } from 'src/common/components/emerald-home-page/emerald-home-page';
import { convertCurrency } from 'src/common/helpers/currency-helper';
import { InterventionsNotFound } from 'src/common/helpers/error-code';
import { AppState } from 'src/common/redux/configure-store';
import { trainingModeStyles } from 'src/common/training-mode/training-mode-styles';
import { popupMessage } from 'src/messages/business-logic/popup-message';
import { themeColor } from 'src/mui-theme';
import { Pagination } from 'src/pagination/pagination';
import { closeSearchModal } from 'src/search/search';
import { getCurrentXLRConfig } from 'src/xlr-configuration/xlr-config';
import { IIntervention, IInterventionInfo } from './interfaces/IIntervention';
import { styles } from './interventions-styles';

export interface IInterventionsProps {
  onClose: () => void;
  resolveAndClose: () => void;
  onSolve: (intervention?: IIntervention) => Promise<void>;
  onVoidItem: (intervention?: IIntervention) => void;
}

type Props = IInterventionsProps;

export const showInterventionsScreen = (props: Props): void => {
  batch((): void => {
    closeAllItems();
    closeSearchModal();
  });
  setHomePage(<InterventionsScreen {...props} />);
};

export const InterventionsScreen: React.FC<Props> = (props: Props): JSX.Element => {
  const { t } = useTranslation();
  const groups = useSelector((state: AppState) => state.interventions.groups);
  const classes = codeContainerStyles();
  const trainingModeClasses = trainingModeStyles();
  const isTrainingMode = useSelector((state: AppState): boolean => state.posMode.trainingMode);
  const { onClose, resolveAndClose, onSolve } = props;
  const [selectedInterventionListItem, setSelectedInterventionListItem] = useState<number>(-1);
  const [selectedInterventionGroup, setSelectedInterventionGroup] = useState<number>(-1);
  const [expanded, setExpanded] = React.useState('');
  /* this is for re-rendering the expansion panel after it's
     animation is finished. 
     TODO: see if we can find a better way
  */
  const [render, setRender] = React.useState(false);
  const [
    { currentPage, lastPage, onPageChanged, navScrollDownEnabled, navScrollUpEnabled },
    scrollRef,
  ] = useScrollButtonState(groups.length);

  useEffect(() => {
    if (selectedInterventionListItem === 0 && selectedInterventionGroup === 0) {
      setExpanded(groups[selectedInterventionGroup]?.groupName);
    }
  }, [selectedInterventionListItem, selectedInterventionGroup, groups]);

  const handleChange = (panel, groupIndex) => (event, isExpanded) => {
    if (isExpanded) {
      setSelectedInterventionGroup(groupIndex);
      setSelectedInterventionListItem(0);
      setExpanded(panel);
    } else {
      setExpanded('');
    }
  };

  const onSolveInterventions = (selectedIntervention?: IIntervention) => {
    const onSuccess = () => {
      setSelectedInterventionGroup(0);
      setSelectedInterventionListItem(0);
    };
    onSolve(selectedIntervention)
      .then(onSuccess)
      .catch(err => {
        if (err.code === InterventionsNotFound) {
          resolveAndClose();
          return;
        }
        popupMessage(err);
      });
  };

  return (
    <Grid item className={`fullHeight flexFull`} style={{ width: '0' }}>
      <Grid item className={`${classes.background} fullWidth fullHeight`}>
        <Grid
          container
          direction='column'
          className={`fullHeight ${classes.codeContainer} ${
            isTrainingMode ? trainingModeClasses.trainingModeBackground : ''
          } ${classes.interventionsContainer}`}
          data-testid='interventions-container'
        >
          <Grid
            item
            container
            className={`${classes.fitHeight}`}
            data-testid='interventions-container-subContainer'
          >
            <Grid
              container
              direction='column'
              className={`${classes.codeContainerTitleHeaderContainer}`}
            >
              <Grid item>
                <Typography
                  variant='h2'
                  className={classes.codeContainerTitleHeader}
                  data-testid='interventions-container-title-text'
                >
                  {t('InterventionsModalTitle')}
                </Typography>
              </Grid>
              <Grid item xs={1}>
                <Typography className={`${classes.codeTitleBar}`} />
              </Grid>
              <Grid item className={classes.subTitleContainer}>
                <Typography
                  variant='body1'
                  className={classes.subTitle}
                  data-testid='interventions-container-sub-title-text'
                >
                  {t('InterventionsModalDescription')}
                </Typography>
              </Grid>
            </Grid>
          </Grid>
          <Grid
            item
            container
            style={{ flex: 1, flexDirection: 'row', justifyContent: 'space-between' }}
          >
            <Grid
              item
              container
              className={`fullWidth ${classes.scrollContainer} ${classes.scrollContainerInterventions}`}
            >
              <div
                ref={scrollRef}
                className='verticalScroll fullWidth fullHeight'
                data-testid='intervention-scroll'
              >
                {groups.map(
                  (group, groupIndex): JSX.Element => {
                    const isSelectedGroup = groupIndex === selectedInterventionGroup;
                    return (
                      <Accordion
                        TransitionProps={{
                          addEndListener: (node, done) => {
                            // use the css transitionend event to mark the finish of a transition
                            node.addEventListener(
                              'transitionend',
                              () => {
                                done();
                                setRender(!render);
                              },
                              false,
                            );
                          },
                        }}
                        className={`${classes.accordion} ${
                          isSelectedGroup ? classes.selected : ''
                        }`}
                        key={`accordion-main-${groupIndex}`}
                        expanded={expanded === group.groupName}
                        onChange={handleChange(group.groupName, groupIndex)}
                      >
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon htmlColor={`${themeColor.primaryMain}`} />}
                          id={`accordion-${groupIndex}`}
                          key={`accordion-${groupIndex}`}
                          data-testid={`intervention-group-line-${groupIndex}`}
                        >
                          <ErrorIcon
                            classes={{ root: `${classes.iconError}` }}
                            key={`icon-${groupIndex}`}
                          ></ErrorIcon>{' '}
                          <Typography
                            variant='body2'
                            data-testid={`intervention-group-${groupIndex}-name`}
                            className={classes.accordionText}
                          >
                            {`${group.groupName} (${group.interventions?.length})`}
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails
                          key={`detail-${groupIndex}`}
                          className={classes.accordionDetails}
                        >
                          <InterventionHeader />
                          {group.interventions?.map(
                            (intervention, index: number): JSX.Element => {
                              const interventionInfo = intervention as IInterventionInfo;
                              const selected =
                                groupIndex === selectedInterventionGroup &&
                                index === selectedInterventionListItem;
                              return (
                                <InterventionsList
                                  interventionInfo={interventionInfo}
                                  key={`accordion-childlist-${index}`}
                                  setSelectedInterventionGroup={() =>
                                    setSelectedInterventionGroup(groupIndex)
                                  }
                                  setSelectedInterventionListItem={() =>
                                    setSelectedInterventionListItem(index)
                                  }
                                  selected={selected}
                                ></InterventionsList>
                              );
                            },
                          )}
                        </AccordionDetails>
                      </Accordion>
                    );
                  },
                )}
              </div>
            </Grid>
            <BottomContainer
              onClose={onClose}
              onSolve={onSolveInterventions}
              selectedIntervention={
                groups[selectedInterventionGroup]?.interventions[selectedInterventionListItem]
              }
              currentPage={currentPage}
              lastPage={lastPage}
              onPageChanged={onPageChanged}
              navScrollUpEnabled={navScrollUpEnabled}
              navScrollDownEnabled={navScrollDownEnabled}
            />
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

const InterventionsList = (props: {
  interventionInfo: IInterventionInfo;
  selected: boolean;
  setSelectedInterventionGroup: () => void;
  setSelectedInterventionListItem: () => void;
}): JSX.Element => {
  const {
    interventionInfo,
    setSelectedInterventionGroup,
    setSelectedInterventionListItem,
    selected,
  } = props;
  const { interventionId: id, itemLookup, itemId, amount } = interventionInfo;
  const classes = styles();
  const { t } = useTranslation();
  return (
    <Grid
      direction='column'
      spacing={0}
      wrap='nowrap'
      container
      onClick={() => {
        setSelectedInterventionGroup();
        setSelectedInterventionListItem();
      }}
      className={`${classes.interventionsItemContainer} ${selected ? classes.selected : ''}`}
      data-testid={`intervention-line-${id}`}
    >
      {/* Main part of interventions items list */}
      <Grid item className='fullHeight'>
        <Grid container justifyContent='flex-start' className={classes.row}>
          <Grid item xs={4} className={classes.interventionsCellContainer}>
            <Grid container item xs={9} className={classes.extendWidth}>
              <Typography
                variant='body2'
                className={classes.interventionsCellInformation}
                data-testid={`intervention-line-${id}-description`}
              >
                {itemLookup?.description ?? t('EmeraldItemNotFound')}
              </Typography>
            </Grid>
          </Grid>
          <Grid item xs={3} className={classes.interventionsCellContainer}>
            <Grid container item xs={9} justifyContent={'center'}>
              <Typography
                variant='body2'
                className={classes.interventionsCellInformation}
                data-testid={`intervention-line-${id}-itemId`}
              >
                {itemId}
              </Typography>
            </Grid>
          </Grid>
          <Grid item xs={3} className={classes.interventionsCellContainer}>
            <Grid container item xs={9} justifyContent={'center'}>
              <Typography
                variant='body2'
                className={classes.interventionsCellInformation}
                data-testid={`intervention-line-${id}-amount`}
              >
                {convertCurrency(amount ?? 0)}
              </Typography>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

const InterventionHeader: React.FC = (): JSX.Element => {
  const classes = styles();
  const { t } = useTranslation();
  return (
    <Grid item>
      <Grid
        direction='column'
        spacing={0}
        wrap='nowrap'
        container
        className={`${classes.interventionsListHeaderContainer}`}
      >
        <Grid item className='fullHeight'>
          <Grid container justifyContent='flex-start' className={classes.row}>
            <Grid item xs={4} className={classes.interventionsListRowContainer}>
              <Grid container item xs={9} className=''>
                <Typography
                  variant='subtitle1'
                  className={classes.interventionsListHeaderText}
                  data-testid={'intervention-list-header-item'}
                >
                  {t('Item')}
                </Typography>
              </Grid>
            </Grid>
            <Grid item xs={3} className={classes.interventionsListRowContainer}>
              <Grid justifyContent={'center'} container item xs={9}>
                <Typography
                  variant='subtitle1'
                  className={classes.interventionsListHeaderText}
                  data-testid={'intervention-header-item-code'}
                >
                  {t('Item_Code')}
                </Typography>
              </Grid>
            </Grid>
            <Grid item xs={3} className={classes.interventionsListRowContainer}>
              <Grid container item xs={9} justifyContent={'center'}>
                <Typography
                  variant='subtitle1'
                  className={classes.interventionsListHeaderText}
                  data-testid={'intervention-amount'}
                >
                  {t('UnitPrice')}
                </Typography>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

const BottomContainer = (props: BottomContainerProps): JSX.Element => {
  const classes = codeContainerStyles();
  const [isSolvable, setIsSolvable] = useState<boolean>(true);
  const { t } = useTranslation();
  const {
    onSolve,
    onClose,
    selectedIntervention,
    currentPage,
    lastPage,
    onPageChanged,
    navScrollUpEnabled,
    navScrollDownEnabled,
  } = props;
  useEffect((): void => {
    if (selectedIntervention) {
      setIsSolvable(!selectedIntervention.approvable && !selectedIntervention.attended);
    } else {
      setIsSolvable(false);
    }
  }, [selectedIntervention]);

  return (
    <Grid container justifyContent='space-between' className={classes.interventionsBottomContainer}>
      <Grid item style={{ position: 'absolute' }}>
        <Grid
          container
          justifyContent='space-between'
          className='fullHeight'
          style={{ width: '5vw' }}
        >
          <Pagination
            currentPage={currentPage}
            totalPages={lastPage}
            updateCurrentPageHandler={onPageChanged}
            testIdPrefix={'interventions'}
            displayText={false}
            canScrollUp={navScrollUpEnabled}
            canScrollDown={navScrollDownEnabled}
          />
        </Grid>
      </Grid>
      <Grid item container justifyContent='center' wrap='nowrap'>
        <BaseButton buttonType='white' onClick={onClose} data-testid='interventions-close-btn'>
          {getCurrentXLRConfig().interventionsSuspendOnCancel
            ? t('SuspendTransactionCommand')
            : t('Cancel')}
        </BaseButton>
        <BaseButton
          data-testid='interventions-solve'
          className={classes.addButton}
          onClick={() => onSolve(selectedIntervention)}
          disabled={!isSolvable}
        >
          {t('Solve')}
        </BaseButton>
      </Grid>
    </Grid>
  );
};
interface BottomContainerProps {
  onSolve: (intervention?: IIntervention) => void;
  onClose: () => void;
  selectedIntervention?: IIntervention;
  currentPage: number;
  lastPage: number;
  onPageChanged: Function;
  navScrollUpEnabled: boolean;
  navScrollDownEnabled: boolean;
}

export default InterventionsScreen;
